import React from "react";

import { getImage } from "gatsby-plugin-image";

import { css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { BgImage } from "gbimage-bridge";

import LinkComponent from "../../LinkComponent";
import Markdown from "../../Markdown";
import HeadlineItem from "../ContentElement/HeadlineItem";
import { SideNavContainer } from "../SideNavContainer";
import useSideNavStore from "../SideNavContainer/store";

const Container = styled.div`
    display: flex;
    flex-direction: column;
    overflow: hidden;
    ${({ theme }) => theme.breakpoints.from.medium.css`
          flex-direction: row;
          column-gap: 50px;
    `}
`;

const RelativeContainer = styled.div`
    position: relative;
`;

const LeftContentContainer = styled.div`
    z-index: 1;
    position: relative;
    ${({ theme }) => theme.paddings.containerOuter.left}
    ${({ theme }) => theme.paddings.containerOuter.right}

    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding-top: 20px;
    padding-bottom: 20px;
    ${({ theme }) => theme.breakpoints.from.medium.css`
         padding-right: 0;
    `}
`;

const LeftShapeContainer = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: -600px;
    background-color: ${({ theme }) => theme.colors.red.toString()};
    border-radius: 40px;
`;

const RightContentContainer = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    ${({ theme }) => theme.breakpoints.from.medium.css`
          left: -300px;
          z-index: 1;
          flex-direction: row;
          column-gap: 50px;
          width: unset;
          height: 100%;
    `}
`;

const RightShapeContainer = styled.div`
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    left: 0;
    overflow: hidden;
    ${({ theme }) => theme.breakpoints.from.medium.css`
        position: absolute;
        right: -600px;
        border-radius: 40px;
    `}
`;

const BottomContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 100%;
    padding-bottom: 20px;
    ${({ theme }) => theme.breakpoints.from.small.css`
        flex-direction: row;
    `}
`;
const ContactItems = styled.div`
    display: flex;
    flex-direction: column;
    font-size: 1.5rem;
    color: ${({ theme }) => theme.colors.white.toString()};
    ${({ theme }) => theme.breakpoints.from.small.css`
      align-self: end;
    `}
`;

const PriceContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: flex-end;
    color: ${({ theme }) => theme.colors.white.toString()};
    ${({ theme }) => theme.breakpoints.upTo.small.css`
      padding-top: 20px;
    `}
`;

const Price = styled.div`
    font-weight: 900;
    line-height: 2rem;
    font-size: 2rem;
    ${({ theme }) => theme.breakpoints.from.small.css`
      font-size: 2.5rem;
    `}
    ${({ theme }) => theme.breakpoints.from.medium.css`
      font-size: 3rem;
      line-height: 4rem;
    `}
    ${({ theme }) => theme.breakpoints.from.normal.css`
      font-size: 3.25rem;
    `}
    ${({ theme }) => theme.breakpoints.from.large.css`
      font-size: 4.5rem;
    `}
`;
const PriceText = styled.div`
    font-size: 1.5rem;
`;

const PriceSubtext = styled.div`
    font-size: 1.5rem;
`;

const Text = styled.div`
    margin: 15px 0;
    font-size: 1.5em;
    color: ${({ theme }) => theme.colors.white.toString()};
    ${({ theme }) => theme.breakpoints.from.medium.css`
      padding-right: 40px;
    `}
    span {
        color: ${({ theme }) => theme.colors.white.toString()};
    }
`;

const Table = styled.div`
    display: flex;
    flex-direction: column;
    max-width: fit-content;
    row-gap: 20px;
    ${({ theme }) => theme.breakpoints.from.small.css`
        row-gap: unset;
    `}
`;

const Row = styled.div`
    display: flex;
    gap: 15px;
    justify-content: space-between;
    font-size: 1.5em;
    color: ${({ theme }) => theme.colors.white.toString()};
`;

const RightColumn = styled.div`
    text-align: right;
    ${({ theme }) => theme.breakpoints.from.small.css`
      text-align: left;
    `}
`;

const LinkItem = styled.a`
    color: ${({ theme }) => theme.colors.white.toString()};

    &:hover {
        color: ${({ theme }) => theme.colors.grey.toString()};
    }
`;

const AdvertisingDoubleShape = ({
    headline,
    headlineNumber = 1,
    topText,
    table,
    bottomText,
    priceText,
    price,
    priceSubtext,
    contactItems,
    image: rawImage,
    nonCmsImageSrc,
}) => {
    const theme = useTheme();
    const [paddingLeft] = useSideNavStore((state) => [state.paddingLeft]);

    const skewHelper = (after = false) => {
        if (after) {
            return "skew(-32deg)";
        }
        return "skew(32deg)";
    };

    const BackgroundColorBlue = css`
        background-color: ${theme.colors.blue.toString()};
    `;

    const widthHelper = (right = false) => {
        if (!right) {
            return css`
                ${theme.breakpoints.propPerEachFrom("width", {
                    extraLarge: "50%",
                    large: "55%",
                    normal: "55%",
                    medium: "50%",
                    small: "100%",
                    extraSmall: "100%",
                })};

                .side-nav-element & {
                    ${theme.breakpoints.propPerEachFrom("width", {
                        extraLarge: "50%",
                        large: "58%",
                        normal: "60%",
                        medium: "63%",
                        small: "100%",
                        extraSmall: "100%",
                    })}
                }
            `;
        }
        return css`
            ${theme.breakpoints.propPerEachFrom("width", {
                extraLarge: "45%",
                large: "40%",
                normal: "40%",
                medium: "45%",
                small: "100%",
                extraSmall: "100%",
            })};

            .side-nav-element & {
                ${theme.breakpoints.propPerEachFrom("width", {
                    extraLarge: "45%",
                    large: "37%",
                    normal: "35%",
                    medium: "32%",
                    small: "100%",
                    extraSmall: "100%",
                })}
            }
        `;
    };

    const leftContainerStyle = css`
        ${widthHelper()};
    `;

    const leftShapeContainerStyle = css`
        ${theme.breakpoints.from.medium.css`
          transform: ${skewHelper()};
        `}
        ${theme.breakpoints.upTo.small.css`
             right: -110px;
        `}
    `;

    const rightContainerStyle = css`
        ${widthHelper(true)};
        ${theme.breakpoints.upTo.small.css`
            height: 300px;
        `}
    `;

    const rightShapeContainerStyle = css`
        ${theme.breakpoints.from.medium.css`
          transform: ${skewHelper()};
        `}
    `;

    const rightContentContainerStyle = css`
        ${theme.breakpoints.from.medium.css`
          transform: ${skewHelper(true)};
        `}
    `;

    const image = rawImage?.src && getImage(rawImage?.src);

    const headlineCss = css`
        font-weight: 900;
        color: ${theme.colors.red.toString()};
        word-wrap: normal;
        ${theme.breakpoints.upTo.medium.css`
            line-height: 2.75rem;

            br {
              display: none;
            }
        `}
        ${theme.breakpoints.upTo.small.css`
            line-height: 2.5rem;
        `}
        ${theme.breakpoints.from.medium.css`
            padding-right: 100px;
        `}
    `;

    const getCss = (bool) => {
        return bool
            ? css`
                  font-weight: 900;
              `
            : ``;
    };

    const formatPhoneForLink = (phone) => {
        return phone.split(" ").join("");
    };

    return (
        <Container>
            <RelativeContainer css={leftContainerStyle}>
                <LeftContentContainer>
                    <SideNavContainer paddingLeft={paddingLeft}>
                        <HeadlineItem
                            color="white"
                            css={headlineCss}
                            headline={headlineNumber}
                            fontSize="4.5rem"
                            fSMedium="3rem"
                            fSNormal="3.25rem"
                            fSSmall="2.5rem"
                            fSExtraSmall="2rem"
                        >
                            {headline}
                        </HeadlineItem>
                        {topText && (
                            <Text>
                                <Markdown>{topText}</Markdown>
                            </Text>
                        )}
                        {table.length > 0 && (
                            <Table>
                                {table.map((row, index) => (
                                    <Row key={index}>
                                        {row.left && <div>{row.left}</div>}
                                        {row.right && (
                                            <RightColumn
                                                css={getCss(row.rightStrong)}
                                            >
                                                {row.right}
                                            </RightColumn>
                                        )}
                                    </Row>
                                ))}
                            </Table>
                        )}
                        {bottomText && (
                            <Text>
                                <Markdown>{bottomText}</Markdown>
                            </Text>
                        )}
                        <BottomContainer>
                            <ContactItems>
                                {contactItems.map((item, index) => (
                                    <div key={index}>
                                        {item.phone && (
                                            <div>
                                                <strong>Tel.:</strong>{" "}
                                                <LinkItem
                                                    href={`tel:${formatPhoneForLink(
                                                        item.phone
                                                    )}`}
                                                >
                                                    {item.phone}
                                                </LinkItem>
                                            </div>
                                        )}
                                        {item.email && (
                                            <div>
                                                <strong>E-Mail:</strong>{" "}
                                                <LinkItem
                                                    href={`mailto:${item.email}`}
                                                >
                                                    {item.email}
                                                </LinkItem>
                                            </div>
                                        )}
                                        {item.linkOptional &&
                                            item.linkOptional.href &&
                                            item.linkOptional.text && (
                                                <LinkComponent
                                                    color="white"
                                                    link={item.linkOptional}
                                                    icon="arrow-right"
                                                />
                                            )}
                                    </div>
                                ))}
                            </ContactItems>
                            {(priceText || price || priceSubtext) && (
                                <PriceContainer>
                                    {priceText && (
                                        <PriceText>{priceText}</PriceText>
                                    )}
                                    {price && <Price>{price} €</Price>}
                                    {priceSubtext && (
                                        <PriceSubtext>
                                            {priceSubtext}
                                        </PriceSubtext>
                                    )}
                                </PriceContainer>
                            )}
                        </BottomContainer>
                    </SideNavContainer>
                </LeftContentContainer>
                <LeftShapeContainer css={leftShapeContainerStyle} />
            </RelativeContainer>
            <RelativeContainer css={rightContainerStyle}>
                <RightShapeContainer
                    css={css`
                        ${rightShapeContainerStyle}
                        ${!image && !nonCmsImageSrc && BackgroundColorBlue}
                    `}
                >
                    {nonCmsImageSrc && (
                        <RightContentContainer
                            css={css`
                                ${rightContentContainerStyle};

                                background-repeat: no-repeat;
                                background-position: center;
                                background-size: cover;
                                background-image: url("${nonCmsImageSrc}");
                            `}
                        />
                    )}
                    <RightContentContainer
                        as={BgImage}
                        {...{ image }}
                        css={css`
                            ${rightContentContainerStyle}
                            ${image &&
                            css`
                                background-repeat: no-repeat;
                                background-position: center;
                                background-size: cover;
                            `};
                        `}
                    />
                </RightShapeContainer>
            </RelativeContainer>
        </Container>
    );
};

export default AdvertisingDoubleShape;
